import { Action, createHook, createStore } from 'react-sweet-state'

import { addNLUExample, deleteNLUExample, putNLUExample } from '../api/nluData'
export interface IEntity {
    entity: string
    start: number
    end: number
    value: string
    tokens: number[]
    confidence_entity?: number
    extractor?: string
}

export interface IToken {
    end: number
    start: number
    value: string
}

export interface INLUDataExample {
    _id: string
    text: string
    intent: string
    entities: IEntity[]
    tokens: IToken[]
}

export interface INLUFilters {
    intents: string[]
    entities: string[]
}

export interface IIntent {
    used: boolean
    name: string
}

type ResponsesState = {
    tabs: string[]
    examples: INLUDataExample[]
    intents: string[]
    intentsStatus: IIntent[]
    entities: string[]
    addingExample: boolean
}

const nluDataInitialState: ResponsesState = {
    tabs: [],
    examples: [],
    intents: [],
    intentsStatus: [],
    entities: [],
    addingExample: false,
}

const nluDataActions = {
    setNLUDataTabs:
        (tabs: string[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ tabs })
        },
    setEntities:
        (entities: string[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ entities })
        },
    setIntents:
        (intents: string[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ intents })
        },
    setIntentsStatus:
        (intentsStatus: IIntent[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ intentsStatus })
        },
    setExamples:
        (examples: INLUDataExample[]): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ examples })
        },
    addExample:
        (
            tab: string,
            { text, intent }: { text: string; intent: string }
        ): Action<ResponsesState> =>
        async ({ setState, getState }) => {
            const example = await addNLUExample(tab, { text, intent }).then(
                (res) => res.data
            )

            const { examples, tabs } = getState()

            if (!getState().tabs.includes(tab)) {
                setState({ tabs: [tab, ...tabs] })
            }

            setState({
                examples: [example, ...examples],
            })
        },

    deleteExample:
        (id: string): Action<ResponsesState> =>
        ({ setState, getState }) => {
            deleteNLUExample(id)
            setState({
                examples: getState().examples.filter(
                    (example) => example._id !== id
                ),
            })
        },
    editExampleMessage:
        (tab: string, id: string, message: string): Action<ResponsesState> =>
        async ({ setState, getState }) => {
            const examples = structuredClone(getState().examples)

            const exampleToEditIndex = examples.findIndex(
                (example) => example._id === id
            )

            if (exampleToEditIndex !== -1) {
                const example = await putNLUExample(tab, {
                    ...examples[exampleToEditIndex],
                    text: message,
                }).then((res) => res.data)
                examples[exampleToEditIndex] = example
            }

            setState({ examples })
        },
    editExampleEntity:
        (
            tab: string,
            id: string,
            entities: IEntity[]
        ): Action<ResponsesState> =>
        async ({ setState, getState }) => {
            const examples = structuredClone(getState().examples)

            const exampleToEditIndex = examples.findIndex(
                (example) => example._id === id
            )

            if (exampleToEditIndex !== -1) {
                const example = await putNLUExample(tab, {
                    ...examples[exampleToEditIndex],
                    entities,
                }).then((res) => res.data)
                examples[exampleToEditIndex] = example
            }

            setState({ examples })
        },
    editExampleIntent:
        (tab: string, id: string, intentName: string): Action<ResponsesState> =>
        async ({ setState, getState }) => {
            const intents = structuredClone(getState().intents)
            const examples = structuredClone(getState().examples)

            const exampleToEditIndex = examples.findIndex(
                (example) => example._id === id
            )

            if (exampleToEditIndex !== -1) {
                const example = await putNLUExample(tab, {
                    ...examples[exampleToEditIndex],
                    intent: intentName,
                }).then((res) => res.data)
                examples[exampleToEditIndex] = example
            }

            setState({ examples })

            if (intents.includes(intentName)) return

            setState({
                intents: [intentName, ...intents],
            })
        },
    setAddingExample:
        (addingExample: boolean): Action<ResponsesState> =>
        ({ setState }) => {
            setState({ addingExample })
        },
}

const Store = createStore({
    initialState: nluDataInitialState,
    actions: nluDataActions,
})

export const useNLUDataStore = createHook(Store)
